/**
 * @module AdapterStatusBadge
 * @package @geenius/adapters/solidjs
 * @description Renders the Tailwind-based adapter status badge for the SolidJS
 * variant. The badge exposes the shared adapter status vocabulary in a compact,
 * themeable visual treatment.
 */

import type { AdapterStatusType } from "@geenius/adapters";
import { cva } from "class-variance-authority";
import type { JSX } from "solid-js";
import { cn } from "../lib/utils";

const LABELS: Record<AdapterStatusType, string> = {
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Error",
  initializing: "Initializing",
};

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-semibold uppercase transition-colors motion-reduce:transition-none",
  {
    variants: {
      status: {
        connected: "border-primary/20 bg-primary/10 text-primary",
        disconnected: "border-border bg-muted text-muted-foreground",
        error: "border-destructive/20 bg-destructive/10 text-destructive",
        initializing: "border-accent/20 bg-accent/10 text-accent",
      },
      size: {
        sm: "text-[0.625rem]",
        md: "text-xs",
      },
    },
    defaultVariants: {
      status: "disconnected",
      size: "sm",
    },
  },
);

const dotVariants = cva("h-1.5 w-1.5 rounded-full", {
  variants: {
    status: {
      connected: "bg-primary",
      disconnected: "bg-muted-foreground",
      error: "bg-destructive",
      initializing: "bg-accent animate-pulse motion-reduce:animate-none",
    },
    size: {
      sm: "",
      md: "h-2 w-2",
    },
  },
  defaultVariants: {
    status: "disconnected",
    size: "sm",
  },
});

/**
 * Props for rendering the SolidJS adapter status badge.
 *
 * @property status Shared adapter status vocabulary value to display.
 * @property showLabel Whether the human-readable label should be rendered.
 * @property size Visual size variant for the badge and status dot.
 * @property class Optional class name merged into the badge wrapper.
 */
export interface AdapterStatusBadgeProps {
  status: AdapterStatusType;
  showLabel?: boolean;
  size?: "sm" | "md";
  class?: string;
}

/**
 * Renders a compact SolidJS badge for the current adapter lifecycle state.
 *
 * @param props Badge props including the status token, optional label visibility, and size variant.
 * @returns A styled badge element that mirrors the shared adapter status taxonomy.
 */
export function AdapterStatusBadge(
  props: AdapterStatusBadgeProps,
): JSX.Element {
  // Icon-only branch: matches the React reference's role="img" + aria-label
  // contract so assistive tech announces the badge as an image with a label
  // rather than seeing an unlabeled decorative span.
  if (props.showLabel === false) {
    return (
      <span
        aria-label={`${LABELS[props.status]} adapter status`}
        role="img"
        class={cn(
          badgeVariants({ status: props.status, size: props.size ?? "sm" }),
          props.class,
        )}
      >
        <span
          aria-hidden="true"
          class={dotVariants({
            status: props.status,
            size: props.size ?? "sm",
          })}
        />
      </span>
    );
  }

  return (
    <span
      class={cn(
        badgeVariants({ status: props.status, size: props.size ?? "sm" }),
        props.class,
      )}
    >
      <span
        aria-hidden="true"
        class={dotVariants({ status: props.status, size: props.size ?? "sm" })}
      />
      <span>{LABELS[props.status]}</span>
    </span>
  );
}
